import { useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Separator,
  Textarea,
} from "@aircall/ds";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";

import { useDeleteEvaluation } from "@/hooks/useDeleteEvaluation/useDeleteEvaluation";
import { EvaluationAccordionHeaderContent } from "../EvaluationAccordionHeaderContent";
import { EvaluationHeading } from "../EvaluationHeading";
import { EvaluationQuestionSummary } from "../EvaluationQuestionSummary";
import {
  isBinaryEvaluationQuestion,
  isRatingEvaluationQuestion,
  transformFormValueIntoInput,
} from "../EvaluationUpdateForm";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import {
  BinaryAnswerValue,
  RatingAnswerValue,
} from "@/graphql/generated/graphql";
import { useBoolean } from "@/hooks/useBoolean/useBoolean";

import { useEvaluation } from "@/hooks/useEvaluation/useEvaluation";
import {
  EvaluationAccordionProps,
  ExcludeNotApplicable,
  QuestionBinaryProps,
  QuestionMultipleProps,
} from ".";

import { useCall } from "@/hooks/useCall/useCall";
import { useCcAccess } from "@/hooks/useCcAccess/useCcAccess";
import { useUpdateEvaluation } from "@/hooks/useUpdateEvaluation/useUpdateEvaluation";
import { getRevieweeUserId } from "../EvaluationNew/EvaluationNew.utils";
import { useTracking } from "./EvaluationResult.hooks";

function MarkAsReviewedForm({
  onSubmit,
  submitting,
}: {
  onSubmit: (feedback: string) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState("");
  const onSubmitClick = () => {
    onSubmit(feedback.trim());
  };
  // render a textarea of global feedback and a button to mark as reviewed
  return (
    <div className="flex flex-col items-end gap-2 px-6 pb-6">
      <Textarea
        name="feedback"
        value={feedback}
        placeholder="Add overall feedback here"
        onChange={(e) => setFeedback(e.target.value)}
      />
      <Button
        variant="secondary"
        size="lg"
        onClick={onSubmitClick}
        disabled={submitting}
      >
        {t("evaluation.mark_as_reviewed")}
      </Button>
    </div>
  );
}

function QuestionBinary({ question }: Readonly<QuestionBinaryProps>) {
  const { binaryAnswer } = question;

  return (
    <EvaluationQuestionSummary<ExcludeNotApplicable<BinaryAnswerValue>>
      key={question.ID}
      value={
        binaryAnswer === "NOT_APPLICABLE" || !binaryAnswer
          ? undefined
          : binaryAnswer
      }
      question={question.questionText}
      rank={question.questionOrder + 1}
      isNotApplicable={binaryAnswer === "NOT_APPLICABLE"}
      comment={question.feedback ?? ""}
      maximumPoints={question.score.totalPoints}
      points={question.score.scorePoints}
      className="p-6"
    />
  );
}

function QuestionMultiple({ question }: Readonly<QuestionMultipleProps>) {
  const { ratingAnswer } = question;

  return (
    <EvaluationQuestionSummary<ExcludeNotApplicable<RatingAnswerValue>>
      key={question.ID}
      value={
        ratingAnswer === "NOT_APPLICABLE" || !ratingAnswer
          ? undefined
          : ratingAnswer
      }
      question={question.questionText}
      rank={question.questionOrder + 1}
      isNotApplicable={ratingAnswer === "NOT_APPLICABLE"}
      comment={question.feedback ?? ""}
      maximumPoints={question.score.totalPoints}
      points={question.score.scorePoints}
      className="p-6"
    />
  );
}

function EvaluationAccordion({ evaluation }: EvaluationAccordionProps) {
  return (
    <Accordion multiple>
      {evaluation.categories.map((category) => (
        <AccordionItem
          key={category.ID}
          value={category.ID}
          data-test={`accordion-item-${category.ID}`}
          className="border-b border-border last:border-b-0"
        >
          <AccordionTrigger data-test={`accordion-header-${category.ID}`}>
            <EvaluationAccordionHeaderContent
              title={category.categoryName}
              points={category.questions.reduce(
                (total, question) => total + question.score.scorePoints,
                0
              )}
              maximumPoints={category.questions.reduce(
                (total, question) => total + question.score.totalPoints,
                0
              )}
            />
          </AccordionTrigger>
          <AccordionContent data-test={`accordion-body-${category.ID}`}>
            {category.questions.map((question) => {
              if (isRatingEvaluationQuestion(question)) {
                return (
                  <QuestionMultiple key={question.ID} question={question} />
                );
              }

              if (isBinaryEvaluationQuestion(question)) {
                return <QuestionBinary key={question.ID} question={question} />;
              }

              throw new Error("Unknown question type.");
            })}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function EvaluationResult() {
  const navigate = useNavigate();

  const { callId } = useParams();

  const { t } = useTranslation();

  const { evaluation, loading } = useEvaluation(callId);
  const { call } = useCall();

  const revieweeUserId = getRevieweeUserId(call);
  const reviewer =
    evaluation?.reviewerTeammate?.entity.__typename === "PhoneTeammate"
      ? evaluation.reviewerTeammate.entity
      : null;

  const { updateEvaluation, loadingUpdateEvaluation } = useUpdateEvaluation();
  const { deleteEvaluation, loadingDeleteEvaluation } = useDeleteEvaluation();

  const handleMarkAsReviewed = (feedback: string) => {
    if (evaluation && revieweeUserId) {
      updateEvaluation(
        transformFormValueIntoInput({
          ID: evaluation.ID,
          feedback,
          revieweeUserId: revieweeUserId,
          categories: evaluation.categories,
        })
      );
    }
  };
  useTracking();

  const {
    value: isConfirmationModalOpen,
    setFalse: closeConfirmationModal,
    setTrue: openConfirmationModal,
  } = useBoolean();

  const editEvaluationHandler = () => {
    navigate("../edit");
  };

  const deleteEvaluationHandler = async () => {
    if (callId) {
      await deleteEvaluation({ callID: callId });
    }
  };
  const { canEdit, canDelete } = useCcAccess();

  if (!evaluation && !loading) {
    return <Navigate to="../empty" />;
  }

  return evaluation ? (
    <>
      <div className="flex flex-col" data-test="evaluation-result-section">
        <EvaluationHeading
          title={evaluation.scoreCardName}
          totalPoints={evaluation.score.scorePoints}
          totalMaximumPoints={evaluation.score.totalPoints}
          comment={evaluation.feedback ?? ""}
          description={evaluation.scoreCardDescription}
          disabled={false}
          canEdit={canEdit}
          canDelete={canDelete}
          onClickEdit={editEvaluationHandler}
          onClickDelete={openConfirmationModal}
          lastReviewAt={evaluation.updatedAt || evaluation.createdAt}
          lastReviewName={reviewer?.fullName}
        />

        {evaluation.isGeneratedByAI && !reviewer && canEdit && (
          <MarkAsReviewedForm
            onSubmit={handleMarkAsReviewed}
            submitting={loadingUpdateEvaluation}
          />
        )}
        <Separator orientation="horizontal" />
        <EvaluationAccordion evaluation={evaluation} />
      </div>
      <ConfirmationModal
        onHide={closeConfirmationModal}
        show={isConfirmationModalOpen}
        loading={loadingDeleteEvaluation}
        actions={[
          {
            label: t("cancel"),
            onClick: closeConfirmationModal,
          },
          {
            variant: "destructive",
            label: t("evaluation.remove_it"),
            onClick: deleteEvaluationHandler,
          },
        ]}
        title={t("evaluation.remove")}
        description={t("evaluation.cancel_evaluation_description", {
          scoreCardName: evaluation.scoreCardName,
        })}
      />
    </>
  ) : null;
}
